/**
 * Emulator Core
 * Main interface for the Nintendo DS emulator using melonDS WASM
 */

import { wasmLoader } from './WasmLoader';

export enum DSButton {
  A = 0,
  B = 1,
  Select = 2,
  Start = 3,
  Right = 4,
  Left = 5,
  Up = 6,
  Down = 7,
  R = 8,
  L = 9,
  X = 10,
  Y = 11,
}

export interface EmulatorState {
  isRunning: boolean;
  isPaused: boolean;
  fps: number;
  romLoaded: boolean;
}

// Type definitions for MelonDS WASM classes
interface WasmEmulator {
  initialize(): void;
  loadFreeBIOS(): void;
  loadUserBIOS(): void;
  setFirmwareSettings(settings: WasmFirmwareSettings): void;
  loadRom(romData: Uint8Array): boolean;
  setSavePath(path: string): void;
  processFrame(): void;
  getTopScreen(): number; // Returns pointer to RGBA data
  getBottomScreen(): number; // Returns pointer to RGBA data
  getAudioBuffer(): number; // Returns pointer to audio samples
  getAudioSamples(): number; // Returns number of samples
  touchScreen(x: number, y: number): void;
  releaseScreen(): void;
  setInput(button: number, pressed: boolean): void;
  getCartTitle(): string;
  getSaveRAM(): number; // Returns pointer to save RAM data
  getSaveRAMSize(): number; // Returns size of save RAM
  setSaveRAM(data: Uint8Array): void; // Load save RAM data
  delete(): void;
}

interface WasmFirmwareSettings {
  setNickname(name: string): void;
  setLanguage(lang: number): void;
  setBirthday(month: number, day: number): void;
  setFirmwareFile(file: string): void;
  delete(): void;
}

interface MelonDSModule {
  WasmEmulator: new () => WasmEmulator;
  WasmFirmwareSettings: new () => WasmFirmwareSettings;
  HEAPU8: Uint8Array;
  HEAPU32: Uint32Array;
  HEAPF32: Float32Array;
}

export class EmulatorCore {
  private module: MelonDSModule | null = null;
  private emulator: WasmEmulator | null = null;
  private isInitialized = false;
  private state: EmulatorState = {
    isRunning: false,
    isPaused: false,
    fps: 0,
    romLoaded: false,
  };

  /**
   * Initialize the emulator
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      console.log('🎮 Loading melonDS WASM module...');

      // Load WASM module
      this.module = (await wasmLoader.load()) as unknown as MelonDSModule;

      console.log('✓ WASM module loaded');

      // Create emulator instance
      if (!this.module.WasmEmulator) {
        throw new Error('WasmEmulator class not found in module');
      }

      this.emulator = new this.module.WasmEmulator();
      console.log('✓ WasmEmulator instance created');

      // Initialize the emulator
      this.emulator.initialize();
      console.log('✓ Emulator initialized');

      // Load free BIOS (built-in, no external files needed)
      this.emulator.loadFreeBIOS();
      console.log('✓ Free BIOS loaded');

      // Configure firmware settings (optional but recommended)
      const firmware = new this.module.WasmFirmwareSettings();
      firmware.setNickname('Player');
      firmware.setLanguage(1); // 0=Japanese, 1=English, 2=French, etc.
      firmware.setBirthday(1, 1); // January 1st
      this.emulator.setFirmwareSettings(firmware);
      firmware.delete(); // Clean up firmware settings object
      console.log('✓ Firmware settings configured');

      this.isInitialized = true;
      console.log('✅ Emulator core fully initialized and ready!');
    } catch (error) {
      console.error('❌ Emulator initialization failed:', error);
      throw error;
    }
  }

  /**
   * Load a ROM into the emulator
   */
  async loadROM(romBuffer: ArrayBuffer): Promise<void> {
    if (!this.emulator || !this.module) {
      throw new Error('Emulator not initialized');
    }

    try {
      console.log(`📦 Loading ROM (${romBuffer.byteLength} bytes)...`);

      // Strict size validation to prevent crashes
      if (romBuffer.byteLength < 0x1000) {
        throw new Error(`ROM file too small (${romBuffer.byteLength} bytes). Minimum size is 4KB.`);
      }

      if (romBuffer.byteLength > 512 * 1024 * 1024) {
        throw new Error(`ROM file too large (${romBuffer.byteLength} bytes). Maximum size is 512MB.`);
      }

      // Convert ArrayBuffer to Uint8Array
      const romData = new Uint8Array(romBuffer);

      // Basic sanity check - ROM should have some non-zero bytes
      let hasData = false;
      for (let i = 0; i < Math.min(512, romData.length); i++) {
        if (romData[i] !== 0) {
          hasData = true;
          break;
        }
      }

      if (!hasData) {
        throw new Error('ROM file appears to be empty or corrupted (all zeros)');
      }

      console.log('✓ ROM size and data checks passed, loading into melonDS...');

      // Load ROM into emulator (wrapped in try-catch to catch WASM exceptions)
      let success = false;
      try {
        success = this.emulator.loadRom(romData);
      } catch (wasmError) {
        console.error('melonDS WASM error:', wasmError);
        throw new Error(`melonDS rejected the ROM: ${wasmError instanceof Error ? wasmError.message : 'Unknown WASM error'}`);
      }

      if (!success) {
        throw new Error('melonDS could not load this ROM (loadRom returned false)');
      }

      this.state.romLoaded = true;

      // Get cart title
      try {
        const title = this.emulator.getCartTitle();
        console.log(`✅ ROM loaded successfully: "${title}"`);
      } catch (e) {
        console.log('✅ ROM loaded successfully (title unavailable)');
      }
    } catch (error) {
      console.error('❌ ROM loading failed:', error);
      this.state.romLoaded = false;
      throw error;
    }
  }

  /**
   * Reset the emulator
   */
  reset(): void {
    if (!this.emulator) {
      throw new Error('Emulator not initialized');
    }

    // Re-initialize the emulator to reset state
    this.emulator.initialize();
    this.state.isPaused = false;
    console.log('✓ Emulator reset');
  }

  /**
   * Run one frame of emulation
   */
  runFrame(): void {
    if (!this.emulator || !this.state.romLoaded) {
      return;
    }

    if (this.state.isPaused) {
      return;
    }

    this.emulator.processFrame();
  }

  /**
   * Get the top screen pixel data
   * Returns RGBA data for 256x192 screen
   */
  getTopScreen(): Uint8Array | null {
    if (!this.emulator || !this.module || !this.state.romLoaded) {
      return null;
    }

    const screenPtr = this.emulator.getTopScreen();
    if (!screenPtr) {
      return null;
    }

    // DS screen is 256x192, 4 bytes per pixel (RGBA)
    const pixelCount = 256 * 192 * 4;
    return new Uint8Array(this.module.HEAPU8.buffer, screenPtr, pixelCount);
  }

  /**
   * Get the bottom screen pixel data
   * Returns RGBA data for 256x192 screen
   */
  getBottomScreen(): Uint8Array | null {
    if (!this.emulator || !this.module || !this.state.romLoaded) {
      return null;
    }

    const screenPtr = this.emulator.getBottomScreen();
    if (!screenPtr) {
      return null;
    }

    // DS screen is 256x192, 4 bytes per pixel (RGBA)
    const pixelCount = 256 * 192 * 4;
    return new Uint8Array(this.module.HEAPU8.buffer, screenPtr, pixelCount);
  }

  /**
   * Get save RAM data
   * Extracts the current save data from the emulator
   */
  getSaveRAM(): Uint8Array | null {
    if (!this.emulator || !this.module || !this.state.romLoaded) {
      console.warn('Cannot get save RAM: emulator not ready or no ROM loaded');
      return null;
    }

    try {
      const saveRAMPtr = this.emulator.getSaveRAM();
      const saveRAMSize = this.emulator.getSaveRAMSize();

      if (!saveRAMPtr || saveRAMSize === 0) {
        console.log('No save RAM available (ROM may not use saves)');
        return null;
      }

      // Copy save RAM from WASM memory
      const saveRAM = new Uint8Array(this.module.HEAPU8.buffer, saveRAMPtr, saveRAMSize);
      // Create a copy to avoid issues with WASM memory changes
      return new Uint8Array(saveRAM);
    } catch (error) {
      console.error('Failed to get save RAM:', error);
      return null;
    }
  }

  /**
   * Set save RAM data
   * Loads save data into the emulator
   */
  setSaveRAM(saveData: Uint8Array): void {
    if (!this.emulator || !this.module || !this.state.romLoaded) {
      console.warn('Cannot set save RAM: emulator not ready or no ROM loaded');
      return;
    }

    try {
      this.emulator.setSaveRAM(saveData);
      console.log(`✓ Save RAM loaded (${saveData.length} bytes)`);
    } catch (error) {
      console.error('Failed to set save RAM:', error);
    }
  }

  /**
   * Get audio buffer
   */
  getAudioBuffer(): Float32Array | null {
    if (!this.emulator || !this.module || !this.state.romLoaded) {
      return null;
    }

    try {
      const bufferPtr = this.emulator.getAudioBuffer();
      const sampleCount = this.emulator.getAudioSamples();

      if (!bufferPtr || sampleCount === 0) {
        return null;
      }

      // Audio data is stereo (2 channels), so total samples * 2
      return new Float32Array(this.module.HEAPF32.buffer, bufferPtr, sampleCount * 2);
    } catch (e) {
      return null;
    }
  }

  /**
   * Set touch screen position
   */
  setTouchPosition(x: number, y: number): void {
    if (!this.emulator) {
      return;
    }

    this.emulator.touchScreen(x, y);
  }

  /**
   * Release touch screen
   */
  releaseTouchPosition(): void {
    if (!this.emulator) {
      return;
    }

    this.emulator.releaseScreen();
  }

  /**
   * Set button state
   */
  setButtonState(button: DSButton, pressed: boolean): void {
    if (!this.emulator) {
      return;
    }

    this.emulator.setInput(button, pressed);
  }

  /**
   * Pause emulation
   */
  pause(): void {
    this.state.isPaused = true;
    this.state.isRunning = false;
  }

  /**
   * Resume emulation
   */
  resume(): void {
    this.state.isPaused = false;
    this.state.isRunning = true;
  }

  /**
   * Toggle pause state
   */
  togglePause(): void {
    if (this.state.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  /**
   * Get current state
   */
  getState(): EmulatorState {
    return { ...this.state };
  }

  /**
   * Update FPS counter
   */
  setFPS(fps: number): void {
    this.state.fps = fps;
  }

  /**
   * Check if emulator is ready
   */
  isReady(): boolean {
    return this.isInitialized && this.state.romLoaded;
  }

  /**
   * Get ROM title
   */
  getROMTitle(): string | null {
    if (!this.emulator || !this.state.romLoaded) {
      return null;
    }

    try {
      return this.emulator.getCartTitle();
    } catch (e) {
      return null;
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.state.isRunning = false;
    this.state.isPaused = false;
    this.state.romLoaded = false;

    // Clean up emulator instance
    if (this.emulator) {
      try {
        this.emulator.delete();
      } catch (e) {
        console.warn('Error cleaning up emulator:', e);
      }
      this.emulator = null;
    }

    this.module = null;
    this.isInitialized = false;

    console.log('✓ Emulator core destroyed');
  }
}
